import { mediaQueriesBP } from "@bevco/Utilities/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonProps } from "@insite/mobius/Button";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, History, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";
import parse from "html-react-parser";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";

export const enum fields {
    backgroundType = "backgroundType",
    backgroundImage = "backgroundImage",
    backgroundVideo = "backgroundVideo",
    overlayOpacity = "overlayOpacity",
    height = "height",
    title = "title",
    description = "description",
    textColor = "textColor",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    buttonVariant = "buttonVariant",
    contentAlignment = "contentAlignment",

    displayBackgroundType = "Background Type",
    displayBackgroundImage = "Background Image",
    displayBackgroundVideo = "Background Video URL",
    displayOverlayOpacity = "Overlay Opacity",
    displayHeight = "Height",
    displayTitle = "Title",
    displayDescription = "Description",
    displayTextColor = "Text Color",
    displayButtonLabel = "CTA Label",
    displayButtonLink = "CTA Link",
    displayButtonVariant = "CTA Variant",
    displayContentAlignment = "Content Alignment",
}

export interface OwnProps extends WidgetProps {
    fields: {
        [fields.backgroundType]: "image" | "video";
        [fields.backgroundImage]: string;
        [fields.backgroundVideo]: string;
        [fields.overlayOpacity]: number;
        [fields.height]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport";
        [fields.title]: string;
        [fields.description]: string;
        [fields.textColor]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonVariant]: "primary" | "secondary" | "tertiary";
        [fields.contentAlignment]: "left" | "center" | "right";
    };
}

export const mapStateToProps = () => ({});

export type Props = HasHistory & OwnProps & ReturnType<typeof mapStateToProps>;

export interface LandingHeroWidgetStyles {
    outerWrapper?: InjectableCss;
    wrapper?: InjectableCss;
    heroContainer?: InjectableCss;
    mediaWrapper?: InjectableCss;
    overlay?: InjectableCss;
    contentWrapper?: InjectableCss;
    contentContainer?: InjectableCss;
    captionWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    heroButton?: ButtonProps;
}

export const landingHeroWidgetStyles: LandingHeroWidgetStyles = {
    outerWrapper: {
        css: css`
            width: 100vw;
            position: relative;
            left: 50%;
            right: 50%;
            margin-left: -50vw;
            margin-right: -50vw;
            overflow-x: hidden;
        `,
    },
    wrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    heroContainer: {
        css: css`
            position: relative;
            width: 100%;
        `,
    },
    mediaWrapper: {
        css: css`
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            width: 100%;
            height: 100%;
            z-index: 1;
            
            img,
            video {
                width: 100%;
                height: 100%;
                object-fit: cover;
                display: block;
            }
        `,
    },

    overlay: {
        css: css`
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 2;
            pointer-events: none;
        `,
    },
    contentWrapper: {
        css: css`
            position: relative;
            z-index: 3;
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
        `,
    },
    contentContainer: {
        css: css`
            width: 100%;
            max-width: 1440px;
            margin: 0 auto;
            padding: 20px;

            ${mediaQueriesBP.minMD} {
                padding: 40px;
            }

            ${mediaQueriesBP.minXLG} {
                padding: 60px 135px;
                
            }
        `,
    },
    captionWrapper: {
        css: css`
            max-width: 100%;

            ${mediaQueriesBP.minMD} {
                max-width: 600px;
            }

            ${mediaQueriesBP.minXLG} {
                max-width: 700px;
            }
        `,
    },
    title: {
        forwardAs: "h1",
        variant: "h1",
        css: css`
            margin-bottom: 16px;
            text-transform: inherit !important;

            ${mediaQueriesBP.minMD} {
                margin-bottom: 20px;
            }

            ${mediaQueriesBP.minXLG} {
                margin-bottom: 24px;
            }
        `,
    },
    description: {
        css: css`
            font-size: 16px;
            line-height: 1.5;
            margin-bottom: 24px;

            ${mediaQueriesBP.minMD} {
                font-size: 18px;
                margin-bottom: 28px;
            }

            ${mediaQueriesBP.minXLG} {
                font-size: 20px;
                line-height: 1.6;
                margin-bottom: 32px;
            }
        `,
    },
    heroButton: {
        mergeCss: true,
        css: css`
            min-width: 140px;
            min-height: 44px;

            ${mediaQueriesBP.minMD} {
                min-width: 180px;
                min-height: 48px;
            }

            ${mediaQueriesBP.minXLG} {
                min-width: 200px;
                min-height: 52px;
            }
        `,
    },
};

const styles = landingHeroWidgetStyles;

const BackgroundVideo = styled.video`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const BackgroundImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const onClick = (url: string | undefined, isInShell: boolean, history: History) => {
    if (!url) return;

    if (url.startsWith("http")) {
        if (isInShell) return;
        window.location.href = url;
    } else {
        history.push(url);
    }
};

export const LandingHeroWidget = ({ fields, history }: Props) => {
    const link = useGetLink(fields.buttonLink);
    const { isInShell } = useShellContext();

    
    let heightStyles: string;
    const isQuarterViewport = fields.height === "1/4 viewport";
    switch (fields.height) {
        case "1/4 viewport":
            heightStyles = "min-height: 40vh;";
            break;
        case "1/2 viewport":
            heightStyles = "min-height: 50vh;";
            break;
        case "3/4 viewport":
            heightStyles = "min-height: 75vh;";
            break;
        case "fullViewport":
            heightStyles = "min-height: 100vh;";
            break;
        default:
            heightStyles = "min-height: 50vh;";
            break;
    }

    const overlayOpacity = fields.overlayOpacity ?? 30;
    const overlayStyle = css`
        ${styles.overlay?.css}
        background-color: rgba(0, 0, 0, ${overlayOpacity / 100});
    `;

    const contentAlignment = fields.contentAlignment ?? "left";
    const getContentAlignment = () => {
        switch (contentAlignment) {
            case "center":
                return "center";
            case "right":
                return "flex-end";
            default:
                return "flex-start";
        }
    };
    const getTextAlignment = () => {
        switch (contentAlignment) {
            case "center":
                return "center";
            case "right":
                return "right";
            default:
                return "left";
        }
    };

    const captionStyle = css`
        ${styles.captionWrapper?.css}
        text-align: ${getTextAlignment()};
        margin-left: ${contentAlignment === "center" ? "auto" : contentAlignment === "right" ? "auto" : "0"};
        margin-right: ${contentAlignment === "center" ? "auto" : contentAlignment === "right" ? "0" : "auto"};
    `;

    const textColor = fields.textColor ?? "#ffffff";

    const titleStyle: TypographyPresentationProps = {
        ...styles.title,
        color: textColor,
    };

    const descriptionStyle: TypographyPresentationProps = {
        ...styles.description,
        css: css`
            ${styles.description?.css}
            p, span {
                color: ${textColor};
            }
        `,
    };

    const outerWrapperStyle = css`
        ${styles.outerWrapper?.css}
        ${heightStyles}
    `;

    const wrapperStyle = css`
        ${styles.wrapper?.css}
        ${heightStyles}
    `;

    const heroContainerStyle = css`
        ${styles.heroContainer?.css}
        ${heightStyles}
    `;

    const contentWrapperStyle = css`
        ${styles.contentWrapper?.css}
        ${heightStyles}
        justify-content: ${getContentAlignment()};
    `;

    const contentContainerStyle = css`
        ${styles.contentContainer?.css}
        ${isQuarterViewport ? "padding: 15px !important;" : ""}
    `;

    const renderBackground = () => {
        if (fields.backgroundType === "video" && fields.backgroundVideo) {
            return (
                <BackgroundVideo autoPlay loop muted playsInline>
                    <source src={fields.backgroundVideo} type="video/mp4" />
                    Your browser does not support the video tag.
                </BackgroundVideo>
            );
        }

        if (fields.backgroundImage) {
            return <BackgroundImage src={fields.backgroundImage} alt="" />;
        }

        return null;
    };

    return (
        <StyledWrapper css={outerWrapperStyle}>
            <StyledWrapper css={wrapperStyle}>
                <StyledWrapper css={heroContainerStyle}>
                    <StyledWrapper {...styles.mediaWrapper}>{renderBackground()}</StyledWrapper>
                    <StyledWrapper css={overlayStyle} />
                    <StyledWrapper css={contentWrapperStyle}>
                        <StyledWrapper css={contentContainerStyle}>
                            <StyledWrapper css={captionStyle}>
                                {fields.title && <Typography {...titleStyle}>{fields.title}</Typography>}
                                {fields.description && (
                                    <Typography {...descriptionStyle}>
                                        {parse(fields.description, parserOptions)}
                                    </Typography>
                                )}
                                {link.url && fields.buttonLabel && (
                                    <Button
                                        {...styles.heroButton}
                                        variant={fields.buttonVariant as any}
                                        onClick={() => onClick(link?.url, isInShell ?? false, history)}
                                    >
                                        {fields.buttonLabel}
                                    </Button>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(LandingHeroWidget)) as any,
    definition: {
        displayName: "Landing Hero",
        group: "Verndale Box" as any,
        icon: "image",
        fieldDefinitions: [
            {
                name: fields.backgroundType,
                displayName: fields.displayBackgroundType,
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Video", value: "video" },
                ],
                defaultValue: "image",
                fieldType: "General",
                isRequired: true,
                sortOrder: 0,
            },
            {
                name: fields.backgroundImage,
                displayName: fields.displayBackgroundImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isVisible: item => item.fields.backgroundType === "image",
                sortOrder: 1,
            },
            {
                name: fields.backgroundVideo,
                displayName: fields.displayBackgroundVideo,
                editorTemplate: "VideoPickerField" as any,
                defaultValue: "",
                fieldType: "General",
                isVisible: item => item.fields.backgroundType === "video",
                sortOrder: 2,
            },
            {
                name: fields.overlayOpacity,
                displayName: fields.displayOverlayOpacity,
                editorTemplate: "IntegerField",
                defaultValue: 30,
                fieldType: "General",
                min: 0,
                max: 100,
                sortOrder: 3,
            },
            {
                name: fields.height,
                displayName: fields.displayHeight,
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "1/4 Viewport", value: "1/4 viewport" },
                    { displayName: "1/2 Viewport", value: "1/2 viewport" },
                    { displayName: "3/4 Viewport", value: "3/4 viewport" },
                    { displayName: "Full Viewport", value: "fullViewport" },
                ],
                defaultValue: "1/2 viewport",
                fieldType: "General",
                hideEmptyOption: true,
                sortOrder: 4,
            },
            {
                name: fields.title,
                displayName: fields.displayTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: false,
                sortOrder: 5,
            },
            {
                name: fields.description,
                displayName: fields.displayDescription,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: false,
                sortOrder: 6,
            },
            {
                name: fields.textColor,
                displayName: fields.displayTextColor,
                editorTemplate: "ColorPickerField",
                defaultValue: "#ffffff",
                fieldType: "General",
                isRequired: false,
                sortOrder: 7,
            },
            {
                name: fields.contentAlignment,
                displayName: fields.displayContentAlignment,
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                defaultValue: "left",
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.buttonLink,
                displayName: fields.displayButtonLink,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                isRequired: false,
                sortOrder: 9,
            },
            {
                name: fields.buttonLabel,
                displayName: fields.displayButtonLabel,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isVisible: item => item.fields.buttonLink != null && item.fields.buttonLink.value,
                sortOrder: 10,
            },
            {
                name: fields.buttonVariant,
                displayName: fields.displayButtonVariant,
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                defaultValue: "primary",
                fieldType: "General",
                isVisible: item => item.fields.buttonLink != null && item.fields.buttonLink.value,
                sortOrder: 11,
            },
        ],
    },
};

export default widgetModule;
